import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "membersList", "memberRow", "contractExtras",
    "categorySelect", "roleSelect", "roleSelectWrapper", "roleHiddenInput",
    "customRoleRow", "customRoleInput",
    "subspecialtiesSection", "subspecialtiesList", "subspecialtyPicker",
    "nonMedicalTasksToggle", "nonMedicalToggle", "nonMedicalTasksSection", "tasksList",
    "customSubspecialtyInput", "customTaskInput",
    "collapseHeader", "collapseBody", "collapseIcon", "displayName",
    "firstNameInput", "lastNameInput", "collapseTags",
    "hoursWarning",
    "removedIds", "staffMemberId"
  ]

  static values = {
    teamId: Number,
    roles: Array,
    subspecialties: Array,
    nonMedicalRoles: Array,
    labelNewMember: { type: String, default: "Nieuw teamlid" },
    labelPlaceholder: { type: String, default: "Placeholder" },
    labelRemove: { type: String, default: "Verwijder" },
    labelHoursWarning: { type: String, default: "Let op: totaal uren overschrijdt 48 uur per week." },
    levelLabels: { type: Object, default: {} }
  }

  toggleCollapse(event) {
    const row = event.target.closest("[data-onboarding-team-members-target='memberRow']")
    if (!row) return

    const body = row.querySelector("[data-onboarding-team-members-target='collapseBody']")
    const icon = row.querySelector("[data-onboarding-team-members-target='collapseIcon']")
    if (!body) return

    const isHidden = body.classList.contains("hidden")
    body.classList.toggle("hidden", !isHidden)
    if (icon) icon.classList.toggle("rotate-180", isHidden)
  }

  nameChanged(event) {
    const row = event.target.closest("[data-onboarding-team-members-target='memberRow']")
    if (!row) return

    const firstName = row.querySelector("[data-onboarding-team-members-target='firstNameInput']")?.value?.trim() || ""
    const lastName = row.querySelector("[data-onboarding-team-members-target='lastNameInput']")?.value?.trim() || ""
    const display = row.querySelector("[data-onboarding-team-members-target='displayName']")

    if (display) {
      const fullName = `${firstName} ${lastName}`.trim()
      display.textContent = fullName || this.labelNewMemberValue
    }
  }

  addMember() {
    const template = this.memberRowTargets[0]
    if (!template) return

    const clone = template.cloneNode(true)
    const newIndex = this.memberRowTargets.length

    clone.querySelectorAll("[name]").forEach(el => {
      el.name = el.name.replace(/members\[\d+\]/, `members[${newIndex}]`)
    })

    clone.querySelectorAll("input[type='text'], input[type='email'], input[type='date'], textarea").forEach(el => {
      el.value = ""
    })
    clone.querySelectorAll("input[type='number']").forEach(el => {
      if (el.name.includes("fte")) {
        el.value = "1.0"
      } else {
        el.value = ""
      }
    })
    clone.querySelectorAll("input[type='hidden'][name*='[role]']").forEach(el => {
      el.value = ""
    })
    clone.querySelectorAll("select").forEach(el => {
      el.selectedIndex = 0
      el.querySelectorAll("option[disabled]").forEach(opt => {
        opt.disabled = false
      })
    })
    clone.querySelectorAll("input[type='checkbox']").forEach(el => {
      el.checked = false
    })
    clone.querySelectorAll("input[type='number'][name*='hours']").forEach(el => {
      el.value = ""
      el.disabled = true
    })

    const roleWrapper = clone.querySelector("[data-onboarding-team-members-target='roleSelectWrapper']")
    if (roleWrapper) roleWrapper.classList.add("hidden")

    const customRoleRow = clone.querySelector("[data-onboarding-team-members-target='customRoleRow']")
    if (customRoleRow) customRoleRow.classList.add("hidden")

    const subsList = clone.querySelector("[data-onboarding-team-members-target='subspecialtiesList']")
    if (subsList) subsList.innerHTML = ""

    const subSection = clone.querySelector("[data-onboarding-team-members-target='subspecialtiesSection']")
    if (subSection) subSection.classList.add("hidden")

    const nmToggle = clone.querySelector("[data-onboarding-team-members-target='nonMedicalTasksToggle']")
    if (nmToggle) nmToggle.classList.add("hidden")

    const nmSection = clone.querySelector("[data-onboarding-team-members-target='nonMedicalTasksSection']")
    if (nmSection) nmSection.classList.add("hidden")

    const extras = clone.querySelectorAll("[data-onboarding-team-members-target='contractExtras']")
    extras.forEach(el => el.classList.add("hidden"))

    const display = clone.querySelector("[data-onboarding-team-members-target='displayName']")
    if (display) display.textContent = this.labelNewMemberValue

    const numberBadge = clone.querySelector(".w-8.h-8.rounded-full")
    if (numberBadge) numberBadge.textContent = newIndex + 1

    const body = clone.querySelector("[data-onboarding-team-members-target='collapseBody']")
    if (body) body.classList.remove("hidden")

    const icon = clone.querySelector("[data-onboarding-team-members-target='collapseIcon']")
    if (icon) icon.classList.add("rotate-180")

    const tags = clone.querySelector("[data-onboarding-team-members-target='collapseTags']")
    if (tags) tags.innerHTML = ""

    clone.classList.remove("border-dashed", "border-gray-300")
    clone.classList.add("border-gray-200")

    this.membersListTarget.appendChild(clone)
  }

  removeMember(event) {
    event.stopPropagation()
    const row = event.target.closest("[data-onboarding-team-members-target='memberRow']")
    if (!row) return

    // Don't allow removing the last member
    if (this.memberRowTargets.length <= 1) return

    // Track removed staff member ID so backend can delete the TeamStaffMember
    const staffIdInput = row.querySelector("[data-onboarding-team-members-target='staffMemberId']")
    if (staffIdInput && staffIdInput.value && this.hasRemovedIdsTarget) {
      const current = this.removedIdsTarget.value
      const ids = current ? current.split(",") : []
      ids.push(staffIdInput.value)
      this.removedIdsTarget.value = ids.join(",")
    }

    row.remove()

    // Re-number remaining rows
    this.memberRowTargets.forEach((r, idx) => {
      const badge = r.querySelector(".w-8.h-8.rounded-full")
      if (badge) badge.textContent = idx + 1

      r.querySelectorAll("[name]").forEach(el => {
        el.name = el.name.replace(/members\[\d+\]/, `members[${idx}]`)
      })
    })
  }

  categoryChanged(event) {
    const category = event.target.value
    const row = event.target.closest("[data-onboarding-team-members-target='memberRow']")
    if (!row) return

    const roleWrapper = row.querySelector("[data-onboarding-team-members-target='roleSelectWrapper']")
    const roleSelect = row.querySelector("[data-onboarding-team-members-target='roleSelect']")
    const customRoleRow = row.querySelector("[data-onboarding-team-members-target='customRoleRow']")
    const hiddenInput = row.querySelector("[data-onboarding-team-members-target='roleHiddenInput']")

    if (!category) {
      if (roleWrapper) roleWrapper.classList.add("hidden")
      if (customRoleRow) customRoleRow.classList.add("hidden")
      if (hiddenInput) hiddenInput.value = ""
      this._hideAllSections(row)
      return
    }

    if (roleWrapper) roleWrapper.classList.remove("hidden")
    if (customRoleRow) customRoleRow.classList.add("hidden")

    if (roleSelect) {
      roleSelect.selectedIndex = 0
      roleSelect.querySelectorAll("option[data-category]").forEach(opt => {
        const cat = opt.dataset.category
        if (cat === "any") return
        opt.classList.toggle("hidden", cat !== category)
        opt.disabled = cat !== category
      })
    }

    if (hiddenInput) hiddenInput.value = ""
    this._hideAllSections(row)
  }

  roleChanged(event) {
    const select = event.target
    const row = select.closest("[data-onboarding-team-members-target='memberRow']")
    if (!row) return

    const customRoleRow = row.querySelector("[data-onboarding-team-members-target='customRoleRow']")
    const hiddenInput = row.querySelector("[data-onboarding-team-members-target='roleHiddenInput']")

    if (select.value === "__custom__") {
      if (customRoleRow) customRoleRow.classList.remove("hidden")
      if (hiddenInput) hiddenInput.value = ""
      this._hideAllSections(row)
      return
    }

    if (customRoleRow) customRoleRow.classList.add("hidden")

    const option = select.options[select.selectedIndex]
    const isMedical = option?.dataset.medical === "true"
    const hasValue = select.value !== ""

    if (hiddenInput) hiddenInput.value = hasValue ? select.value : ""

    const duties = row.querySelector("input[type='checkbox'][name$='[available_for_duties]']")
    if (duties && hasValue) duties.checked = isMedical

    this._applySectionVisibility(row, isMedical, hasValue)
  }

  confirmCustomRole(event) {
    const row = event.target.closest("[data-onboarding-team-members-target='memberRow']")
    if (!row) return

    const input = row.querySelector("[data-onboarding-team-members-target='customRoleInput']")
    if (!input || !input.value.trim()) return

    const name = input.value.trim()

    const catSelect = row.querySelector("[data-onboarding-team-members-target='categorySelect']")
    const category = catSelect?.value || "medical"
    const isMedical = category === "medical"

    const roleSelect = row.querySelector("[data-onboarding-team-members-target='roleSelect']")
    if (roleSelect) {
      const newOpt = document.createElement("option")
      newOpt.value = name
      newOpt.textContent = name
      newOpt.dataset.medical = isMedical ? "true" : "false"
      newOpt.dataset.category = category

      const customOpt = roleSelect.querySelector("option[value='__custom__']")
      roleSelect.insertBefore(newOpt, customOpt)
      roleSelect.value = name
    }

    const hiddenInput = row.querySelector("[data-onboarding-team-members-target='roleHiddenInput']")
    if (hiddenInput) hiddenInput.value = name

    const customRoleRow = row.querySelector("[data-onboarding-team-members-target='customRoleRow']")
    if (customRoleRow) customRoleRow.classList.add("hidden")
    input.value = ""

    const duties = row.querySelector("input[type='checkbox'][name$='[available_for_duties]']")
    if (duties) duties.checked = isMedical

    this._applySectionVisibility(row, isMedical, true)
  }

  _hideAllSections(row) {
    const subSection = row.querySelector("[data-onboarding-team-members-target='subspecialtiesSection']")
    const nmToggle = row.querySelector("[data-onboarding-team-members-target='nonMedicalTasksToggle']")
    const nmSection = row.querySelector("[data-onboarding-team-members-target='nonMedicalTasksSection']")

    if (subSection) subSection.classList.add("hidden")
    if (nmToggle) {
      nmToggle.classList.add("hidden")
      const cb = nmToggle.querySelector("input[type='checkbox']")
      if (cb) cb.checked = false
    }
    if (nmSection) nmSection.classList.add("hidden")
  }

  _applySectionVisibility(row, isMedical, hasValue) {
    const subSection = row.querySelector("[data-onboarding-team-members-target='subspecialtiesSection']")
    const nmToggle = row.querySelector("[data-onboarding-team-members-target='nonMedicalTasksToggle']")
    const nmSection = row.querySelector("[data-onboarding-team-members-target='nonMedicalTasksSection']")

    if (subSection) {
      subSection.classList.toggle("hidden", !isMedical || !hasValue)
    }
    if (nmToggle) {
      nmToggle.classList.toggle("hidden", !isMedical || !hasValue)
      if (!isMedical) {
        const toggle = nmToggle.querySelector("input[type='checkbox']")
        if (toggle) toggle.checked = false
      }
    }
    if (nmSection) {
      if (!isMedical && hasValue) {
        nmSection.classList.remove("hidden")
      } else if (isMedical) {
        const toggle = row.querySelector("[data-onboarding-team-members-target='nonMedicalToggle']")
        nmSection.classList.toggle("hidden", !toggle?.checked)
      } else {
        nmSection.classList.add("hidden")
      }
    }
  }

  contractChanged(event) {
    const select = event.target
    const row = select.closest("[data-onboarding-team-members-target='memberRow']")
    if (!row) return

    const extras = row.querySelectorAll("[data-onboarding-team-members-target='contractExtras']")
    const needsExtras = select.value !== "fulltime"
    extras.forEach(el => el.classList.toggle("hidden", !needsExtras))

    this._checkHoursLimit(row)
  }

  toggleNonMedicalTasks(event) {
    const row = event.target.closest("[data-onboarding-team-members-target='memberRow']")
    if (!row) return

    const section = row.querySelector("[data-onboarding-team-members-target='nonMedicalTasksSection']")
    if (section) {
      section.classList.toggle("hidden", !event.target.checked)
    }
  }

  _checkHoursLimit(row) {
    const hpw = row.querySelector("input[name*='[hours_per_week]']")
    const totalHours = hpw && hpw.value ? parseFloat(hpw.value) || 0 : 0

    const warning = row.querySelector("[data-onboarding-team-members-target='hoursWarning']")
    if (warning) {
      warning.classList.toggle("hidden", totalHours <= 48)
    }
  }

  placeholderToggled(event) {
    const row = event.target.closest("[data-onboarding-team-members-target='memberRow']")
    if (!row) return

    const isPlaceholder = event.target.checked
    const display = row.querySelector("[data-onboarding-team-members-target='displayName']")

    if (isPlaceholder) {
      row.classList.add("border-dashed", "border-gray-300")
      row.classList.remove("border-gray-200")
      if (display && !display.textContent.trim()) {
        display.textContent = this.labelPlaceholderValue
      }
    } else {
      row.classList.remove("border-dashed", "border-gray-300")
      row.classList.add("border-gray-200")
    }
  }

  addFromPicker(event) {
    const picker = event.target
    if (!picker.value) return

    const row = picker.closest("[data-onboarding-team-members-target='memberRow']")
    if (!row) return

    const selected = picker.options[picker.selectedIndex]
    const key = selected.value
    const name = selected.dataset.name || selected.textContent.trim()

    this._addSubspecialtyCard(row, key, name, 5)

    selected.disabled = true
    picker.selectedIndex = 0
  }

  removeSubspecialty(event) {
    const card = event.target.closest(".subspecialty-card")
    if (!card) return

    const row = card.closest("[data-onboarding-team-members-target='memberRow']")
    const subKey = card.dataset.subKey

    if (row && subKey) {
      const picker = row.querySelector("[data-onboarding-team-members-target='subspecialtyPicker']")
      if (picker) {
        const opt = picker.querySelector(`option[value="${subKey}"]`)
        if (opt) opt.disabled = false
      }
    }

    card.remove()
  }

  addCustomSubspecialty(event) {
    const row = event.target.closest("[data-onboarding-team-members-target='memberRow']")
    if (!row) return

    const input = row.querySelector("[data-onboarding-team-members-target='customSubspecialtyInput']")
    if (!input || !input.value.trim()) return

    const name = input.value.trim()
    const key = "custom_" + name.toLowerCase().replace(/[^a-z0-9]+/g, "_")

    this._addSubspecialtyCard(row, key, name, 5)
    input.value = ""
  }

  _addSubspecialtyCard(row, key, name, initialLevel) {
    const list = row.querySelector("[data-onboarding-team-members-target='subspecialtiesList']")
    if (!list) return

    const firstNameInput = row.querySelector("[data-onboarding-team-members-target='firstNameInput']")
    const prefix = firstNameInput?.name?.replace("[first_name]", "") || ""

    const isCustom = key.startsWith("custom_")
    const bgClass = isCustom ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200"

    const card = document.createElement("div")
    card.className = `subspecialty-card flex flex-col gap-1.5 p-3 rounded-xl border ${bgClass}`
    card.dataset.subKey = key

    card.innerHTML = `
      <div class="flex items-center justify-between">
        <span class="font-medium text-sm text-gray-800">${name}</span>
        <button type="button" class="text-gray-400 hover:text-red-500 text-xs"
                data-action="click->onboarding-team-members#removeSubspecialty"
                title="${this.labelRemoveValue}">
          &#10005;
        </button>
      </div>
      <div class="flex items-center gap-2">
        <input type="range" min="0" max="10" step="1" value="${initialLevel}"
               name="${prefix}[subspecialties][${key}]"
               class="flex-1 h-2 accent-[var(--pu-teal)] cursor-pointer"
               data-action="input->onboarding-team-members#levelSliderChanged">
        <span class="level-badge w-16 text-center text-xs font-semibold px-2 py-0.5 rounded-full"></span>
      </div>
    `

    list.appendChild(card)
    this._updateLevelBadge(card, initialLevel)
  }

  levelSliderChanged(event) {
    const slider = event.target
    const card = slider.closest(".subspecialty-card")
    if (!card) return

    this._updateLevelBadge(card, parseInt(slider.value))
  }

  _updateLevelBadge(card, val) {
    const badge = card.querySelector(".level-badge")
    if (!badge) return

    const ll = this.levelLabelsValue || {}
    const labels = { 0: "\u2014", 1: ll.trainee || "Trainee", 2: ll.trainee || "Trainee", 3: ll.junior || "Junior", 4: ll.junior || "Junior", 5: ll.mid || "Mid", 6: ll.mid || "Mid", 7: ll.senior || "Senior", 8: ll.senior || "Senior", 9: ll.expert || "Expert", 10: ll.expert || "Expert" }
    const colors = {
      0: "bg-gray-200 text-gray-500",
      1: "bg-blue-100 text-blue-700", 2: "bg-blue-100 text-blue-700",
      3: "bg-green-100 text-green-700", 4: "bg-green-100 text-green-700",
      5: "bg-yellow-100 text-yellow-700", 6: "bg-yellow-100 text-yellow-700",
      7: "bg-orange-100 text-orange-700", 8: "bg-orange-100 text-orange-700",
      9: "bg-red-100 text-red-700", 10: "bg-red-100 text-red-700"
    }

    badge.textContent = labels[val] || "\u2014"
    badge.className = `level-badge w-16 text-center text-xs font-semibold px-2 py-0.5 rounded-full ${colors[val] || colors[0]}`
  }

  addCustomTask(event) {
    const row = event.target.closest("[data-onboarding-team-members-target='memberRow']")
    if (!row) return

    const input = row.querySelector("[data-onboarding-team-members-target='customTaskInput']")
    if (!input || !input.value.trim()) return

    const name = input.value.trim()
    const key = name.toLowerCase().replace(/[^a-z0-9]+/g, "_")
    const list = row.querySelector("[data-onboarding-team-members-target='tasksList']")
    if (!list) return

    const firstNameInput = row.querySelector("[data-onboarding-team-members-target='firstNameInput']")
    const prefix = firstNameInput?.name?.replace("[first_name]", "") || ""

    const taskDiv = document.createElement("div")
    taskDiv.className = "flex items-center gap-3 p-2 bg-blue-50 rounded-lg border border-blue-200"

    taskDiv.innerHTML = `
      <input type="checkbox" name="${prefix}[non_medical_tasks][${key}][enabled]" value="1"
             class="h-4 w-4 text-[var(--pu-teal)] border-gray-300 rounded focus:ring-[var(--pu-teal)]"
             checked>
      <span class="flex-1 text-sm font-medium text-gray-800">${name}</span>
    `

    list.appendChild(taskDiv)
    input.value = ""
  }
}
